import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, ButtonGroup, Button } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GridOnIcon from '@mui/icons-material/GridOn';
import SpeedIcon from '@mui/icons-material/Speed';
import BoltIcon from '@mui/icons-material/Bolt';
import FlexService from '../../services/FlexService';

const LAYOUT_STORAGE_KEY = 'fastHome.dashboardLayout';
const CARD_WIDTH = 220;

// Every widget a slot can be set to. Each entry is self-contained (its own
// script + destination workspace + icon) so adding a new predefined widget
// later is a one-line addition here -- see project_home_dashboard_kpi_widgets_idea.
const WIDGET_CATALOG = [
  {
    id: 'inbound_open_receipts',
    columnLabel: 'Inbound',
    icon: MoveToInboxIcon,
    scriptName: 'kpi_open_receipts_count',
    workspaceCode: 'RECEIVING',
    workspaceTitle: 'Receiving',
  },
  {
    id: 'inventory_needing_attention',
    columnLabel: 'Inventory',
    icon: Inventory2Icon,
    scriptName: 'kpi_onhold_inventory_count',
    workspaceCode: 'INVENTORY',
    workspaceTitle: 'Inventory',
  },
  {
    id: 'outbound_open_orders',
    columnLabel: 'Outbound',
    icon: LocalShippingIcon,
    scriptName: 'kpi_open_orders_count',
    workspaceCode: 'SHIPMENT_PLANNING', // Outbound: its Orders tab (FH-203); there is no ORDERS workspace
    workspaceTitle: 'Outbound',
  },
  {
    id: 'sla_risk',
    columnLabel: 'SLA',
    icon: WarningAmberIcon,
    scriptName: 'kpi_sla_risk_count',
    workspaceCode: 'SHIPMENT_PLANNING', // Outbound: its Orders tab (FH-203); there is no ORDERS workspace
    workspaceTitle: 'Outbound',
  },
  {
    id: 'zone_congestion',
    columnLabel: 'Zones',
    icon: GridOnIcon,
    scriptName: 'kpi_zone_congestion',
    workspaceCode: 'DOCK_MANAGEMENT',
    workspaceTitle: 'Zones',
  },
  {
    id: 'labor_productivity',
    columnLabel: 'Labor',
    icon: SpeedIcon,
    scriptName: 'kpi_labor_productivity_pct',
    workspaceCode: 'SHIPMENT_PLANNING', // Outbound: its Orders tab (FH-203); there is no ORDERS workspace
    workspaceTitle: 'Outbound',
  },
  {
    id: 'outbound_picks_per_hour',
    columnLabel: 'Picking',
    icon: BoltIcon,
    scriptName: 'kpi_outbound_picks_per_hour',
    workspaceCode: 'SHIPMENT_PLANNING', // Outbound: its Orders tab (FH-203); there is no ORDERS workspace
    workspaceTitle: 'Outbound',
  },
];

const WIDGET_BY_ID = Object.fromEntries(WIDGET_CATALOG.map((w) => [w.id, w]));
const DEFAULT_SLOT_IDS = WIDGET_CATALOG.map((w) => w.id);
const DEFAULT_COLUMNS = 4;

// columnsIsDefault tracks whether the user has ever explicitly picked a
// column count in THIS browser -- while true, the server-side default
// (get_home_dashboard_columns, tenant-overridable via lookup_values
// DASHBOARD_CONFIG/HOME_DASHBOARD_COLUMNS) is allowed to update `columns`
// on mount. The moment the user clicks a column-count button, this flips
// to false and their explicit choice wins from then on, in this browser.
const loadLayout = () => {
  try {
    const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!saved) return { columns: DEFAULT_COLUMNS, columnsIsDefault: true, slotIds: DEFAULT_SLOT_IDS };
    const parsed = JSON.parse(saved);
    const slotIds = Array.isArray(parsed.slotIds) ? parsed.slotIds.filter((id) => WIDGET_BY_ID[id]) : DEFAULT_SLOT_IDS;
    return {
      columns: [2, 3, 4, 5, 6].includes(parsed.columns) ? parsed.columns : DEFAULT_COLUMNS,
      columnsIsDefault: parsed.columnsIsDefault !== false,
      slotIds: slotIds.length > 0 ? slotIds : DEFAULT_SLOT_IDS,
    };
  } catch {
    return { columns: DEFAULT_COLUMNS, columnsIsDefault: true, slotIds: DEFAULT_SLOT_IDS };
  }
};

/**
 * Post-login home dashboard -- a fixed-position grid of KPI widget slots.
 * Each slot has its own settings (gear) icon to swap in a different
 * predefined widget from WIDGET_CATALOG -- positions stay fixed, only the
 * widget occupying a slot changes. Columns are user-configurable; as more
 * slots are added the grid grows downward (new rows), with both scroll
 * directions available once content exceeds the viewport (deliberately
 * fixed-width cards, not fluid, so a wide/tall layout genuinely needs to
 * scroll rather than reflow -- per explicit user direction 2026-09-28).
 *
 * Layout (which widget is in which slot, and the user's own chosen column
 * count once they pick one) persists to localStorage, not a backend
 * preference row -- "for now it's just being able to select a predefined
 * widget," full per-user/role-based server-side config is a deliberately
 * deferred follow-on. The column count's DEFAULT (before any personal
 * choice) does come from the server though, tenant/warehouse-overridable
 * via DASHBOARD_CONFIG/HOME_DASHBOARD_COLUMNS in lookup_values -- per user
 * direction 2026-09-28. See project_home_dashboard_kpi_widgets_idea.
 */
function HomeDashboard({ colors, onOpenWorkspace }) {
  const [layout, setLayout] = useState(loadLayout);
  const [values, setValues] = useState({}); // widgetId -> { value, label } | 'error' | undefined (loading)
  const [menuAnchor, setMenuAnchor] = useState(null); // { el, slotIndex } | null

  const persistLayout = useCallback((next) => {
    setLayout(next);
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // best-effort -- a failed persist just means the layout resets next visit
    }
  }, []);

  const loadWidget = useCallback(async (widgetId) => {
    const widget = WIDGET_BY_ID[widgetId];
    if (!widget) return;
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timed out')), 15000));
    try {
      const response = await Promise.race([
        FlexService.executeScript({ scriptName: widget.scriptName }),
        timeout,
      ]);
      const result = response?.context?.result;
      setValues((prev) => ({ ...prev, [widgetId]: result && typeof result.value === 'number' ? result : 'error' }));
    } catch (err) {
      console.error(`Home dashboard: failed to load ${widget.scriptName}:`, err);
      setValues((prev) => ({ ...prev, [widgetId]: 'error' }));
    }
  }, []);

  // Load every widget currently occupying a slot. Re-runs when the layout
  // changes (a swap) -- loadWidget itself doesn't re-fetch anything already
  // cached in `values`, so this is cheap on repeat calls.
  useEffect(() => {
    layout.slotIds.forEach((id) => {
      setValues((prev) => (id in prev ? prev : { ...prev, [id]: undefined }));
      loadWidget(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.slotIds]);

  // Fetch the tenant-configurable default column count once on mount, but
  // only apply it while the user hasn't made their own explicit choice in
  // this browser yet (columnsIsDefault) -- an explicit choice always wins
  // after that. See get_home_dashboard_columns / DASHBOARD_CONFIG.
  useEffect(() => {
    let cancelled = false;
    FlexService.executeScript({ scriptName: 'get_home_dashboard_columns' })
      .then((response) => {
        if (cancelled) return;
        const value = response?.context?.result?.value;
        if (typeof value === 'number' && [2, 3, 4, 5, 6].includes(value)) {
          setLayout((prev) => (prev.columnsIsDefault ? { ...prev, columns: value } : prev));
        }
      })
      .catch((err) => console.warn('Home dashboard: failed to load default column count:', err));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenMenu = (e, slotIndex) => {
    e.stopPropagation();
    setMenuAnchor({ el: e.currentTarget, slotIndex });
  };

  const handleCloseMenu = () => setMenuAnchor(null);

  const handleSelectWidget = (widgetId) => {
    if (menuAnchor == null) return;
    const nextSlotIds = [...layout.slotIds];
    nextSlotIds[menuAnchor.slotIndex] = widgetId;
    persistLayout({ ...layout, slotIds: nextSlotIds });
    if (!(widgetId in values)) loadWidget(widgetId);
    setMenuAnchor(null);
  };

  const setColumns = (n) => persistLayout({ ...layout, columns: n, columnsIsDefault: false });

  const gridWidth = useMemo(() => layout.columns * CARD_WIDTH + (layout.columns - 1) * 24, [layout.columns]);

  return (
    <Box sx={{ height: '100%', overflow: 'auto', bgcolor: colors.background, p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 0.5 }}>
            Warehouse Health
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textMuted }}>
            Click a card to open what it's counting. Use a widget's gear icon to swap it for another.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>Columns</Typography>
          <ButtonGroup size="small">
            {[2, 3, 4, 5, 6].map((n) => (
              <Button
                key={n}
                variant={layout.columns === n ? 'contained' : 'outlined'}
                onClick={() => setColumns(n)}
                sx={{
                  minWidth: 32,
                  bgcolor: layout.columns === n ? '#22c55e' : 'transparent',
                  borderColor: colors.border,
                  color: layout.columns === n ? '#fff' : colors.textSecondary,
                  '&:hover': { bgcolor: layout.columns === n ? '#16a34a' : colors.hoverBg },
                }}
              >
                {n}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      </Box>

      {/* Fixed-width cards, deliberately not fluid -- both scroll
          directions are expected once the grid outgrows the viewport
          (more widgets -> new rows -> vertical scroll; more columns than
          fit -> horizontal scroll), per explicit user direction. */}
      <Box sx={{ overflow: 'auto', pb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${layout.columns}, ${CARD_WIDTH}px)`,
            gap: 3,
            width: gridWidth,
          }}
        >
          {layout.slotIds.map((widgetId, slotIndex) => {
            const widget = WIDGET_BY_ID[widgetId];
            if (!widget) return null;
            const state = values[widgetId];
            const Icon = widget.icon;
            const loading = state === undefined;
            const errored = state === 'error';
            return (
              <Box
                key={slotIndex}
                onClick={() => onOpenWorkspace(widget.workspaceCode, widget.workspaceTitle, false)}
                sx={{
                  position: 'relative',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 1,
                  bgcolor: colors.surface,
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, transform 0.1s',
                  '&:hover': { borderColor: '#22c55e', transform: 'translateY(-2px)' },
                }}
              >
                <Tooltip title="Change this widget">
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenMenu(e, slotIndex)}
                    sx={{ position: 'absolute', top: 4, right: 4, color: colors.textMuted, '&:hover': { color: '#22c55e' } }}
                  >
                    <SettingsIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Typography variant="overline" sx={{ color: colors.textMuted, fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                  {widget.columnLabel}
                </Typography>
                <Icon sx={{ fontSize: 28, color: '#22c55e' }} />
                {loading ? (
                  <CircularProgress size={28} sx={{ my: 1 }} />
                ) : errored ? (
                  <Typography variant="h4" sx={{ color: colors.textMuted, fontWeight: 700 }}>—</Typography>
                ) : (
                  <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                    {state.value.toLocaleString()}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center' }}>
                  {errored ? 'Could not load' : (state?.label || '...')}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        {WIDGET_CATALOG.map((w) => {
          const WIcon = w.icon;
          const isCurrent = menuAnchor != null && layout.slotIds[menuAnchor.slotIndex] === w.id;
          return (
            <MenuItem key={w.id} selected={isCurrent} onClick={() => handleSelectWidget(w.id)}>
              <ListItemIcon>
                <WIcon sx={{ fontSize: 20, color: isCurrent ? '#22c55e' : undefined }} />
              </ListItemIcon>
              <ListItemText primary={w.columnLabel} secondary={values[w.id]?.label} />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

export default HomeDashboard;
